import { useEffect, useState } from "react";

import { CONSENT_EVENT, readConsent, resetConsent, saveConsent } from "@/lib/consent";

/**
 * Bouton de révocation/gestion du consentement : réaffiche le bandeau.
 */
export function CookiePreferencesButton({ className = "" }: { className?: string }) {
  return (
    <button type="button" onClick={() => resetConsent()} className={className}>
      Gérer mes cookies
    </button>
  );
}

/**
 * Banner de consentement RGPD, auto-hébergé (aucun script tiers, compatible CSP stricte).
 * Deux catégories : nécessaire (toujours actif) et mesure d'audience (désactivée par défaut).
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [preferences, setPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    setVisible(readConsent() === null);
    const onChange = () => setVisible(readConsent() === null);
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  if (!visible) return null;

  const decide = (value: boolean) => {
    saveConsent(value);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Préférences de cookies"
      className="fixed inset-x-0 bottom-0 z-[70] px-4 pb-[calc(env(safe-area-inset-bottom)+5.5rem)] md:pb-6"
    >
      <div className="glass mx-auto max-w-2xl rounded-2xl p-5 shadow-lg">
        <p className="font-serif text-lg">Nous utilisons des cookies</p>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          Ce site n’utilise que des données strictement nécessaires à son fonctionnement, ainsi
          qu’une mesure d’audience interne (nombre de consultations des contenus), désactivée tant
          que vous ne l’avez pas acceptée. Aucun cookie publicitaire, aucun traceur tiers. Votre
          choix est conservé 13 mois et reste modifiable à tout moment.
        </p>

        {preferences && (
          <div className="mt-4 space-y-3 border-t border-border pt-4 text-xs">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-foreground">Strictement nécessaires</p>
                <p className="text-muted-foreground">
                  Indispensables à l’affichage du site, stockés dans votre navigateur uniquement :
                </p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4 text-muted-foreground">
                  <li>préférence de thème clair/sombre ;</li>
                  <li>mémorisation de ce choix de consentement (13 mois) ;</li>
                  <li>cache de navigation des contenus (affichage plus rapide) ;</li>
                  <li>animation d’introduction déjà vue (session en cours).</li>
                </ul>
              </div>
              <span className="whitespace-nowrap text-muted-foreground">Toujours actifs</span>
            </div>
            <label className="flex items-start justify-between gap-4">
              <span>
                <span className="block font-medium text-foreground">Mesure d’audience</span>
                <span className="text-muted-foreground">
                  Comptage anonyme des contenus consultés, sans adresse IP ni identifiant de
                  visiteur.
                </span>
              </span>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-1 h-4 w-4 accent-ocean"
                aria-label="Autoriser la mesure d’audience"
              />
            </label>
          </div>
        )}

        <div className="mt-5 flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-end">
          {!preferences && (
            <button
              type="button"
              onClick={() => setPreferences(true)}
              className="px-4 py-2 text-foreground/80 transition-colors hover:text-foreground"
            >
              Préférences
            </button>
          )}
          <button
            type="button"
            onClick={() => decide(false)}
            className="rounded-full border border-foreground px-5 py-2 transition-colors hover:bg-foreground hover:text-paper"
          >
            Refuser
          </button>
          <button
            type="button"
            onClick={() => decide(preferences ? analytics : true)}
            className="rounded-full bg-ocean px-5 py-2 text-paper transition-colors hover:bg-ocean-700"
          >
            {preferences ? "Enregistrer" : "Tout accepter"}
          </button>
        </div>
      </div>
    </div>
  );
}
